import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Paper, TableBody, Typography } from "@mui/material";
import { GroupsOutlined } from "@mui/icons-material";
import { CeldaKit, CeldaPrincipal, Columna, FilaKit, TablaKit } from "../../components/kit/TablaKit";
import Etiqueta from "../../components/kit/Etiqueta";
import { useCohortesComision } from "../../queries/useComisiones";
import { CohorteComision } from "../../types/comision";
import { Accesores, ordenar, useOrden } from "../../utils/orden";

const COLUMNAS: (string | Columna)[] = [
  { label: "Etapa", sortKey: "etapa" },
  { label: "Comisión", sortKey: "tamano" },
  { label: "Entregados", sortKey: "total" },
  { label: "Sin entregar", sortKey: "sin_entregar" },
  { label: "Con comisión", sortKey: "con" },
  { label: "Faltan", sortKey: "faltan" },
  { label: "Acción", align: "right" as const },
];

const ACC_COHORTE: Accesores<CohorteComision> = {
  etapa: (c) => c.paso_nombre,
  tamano: (c) => c.tamano_comision,
  total: (c) => c.total,
  sin_entregar: (c) => c.sin_entregar,
  con: (c) => c.con_comision,
  faltan: (c) => c.faltan,
};

/**
 * "Comisiones" (coordinador): las cohortes (etapa + período) abiertas. Cada una se
 * administra en su propia pantalla (`ComisionCohorte`), que es una ruta y no un diálogo
 * para poder volver con el navegador. Solo staff (el backend responde 403 al resto).
 */
export default function Comisiones() {
  const { data, isLoading, isError } = useCohortesComision();
  const cohortes = useMemo(() => data ?? [], [data]);
  const navigate = useNavigate();
  const { orden, onSort } = useOrden();

  // Agrupa por período preservando el orden del backend (período desc, etapa asc).
  const grupos = useMemo(() => {
    const map = new Map<string, { label: string; cohortes: CohorteComision[] }>();
    for (const c of cohortes) {
      const key = `${c.ano}.${c.semestre}`;
      if (!map.has(key)) map.set(key, { label: `${c.ano} · ${c.semestre_display}`, cohortes: [] });
      map.get(key)!.cohortes.push(c);
    }
    return [...map.values()];
  }, [cohortes]);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Comisiones
      </Typography>

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {isError && <Alert severity="error">No se pudieron cargar las cohortes (¿tienes permisos de coordinación?).</Alert>}

      {!isLoading && !isError && cohortes.length === 0 && (
        <Paper elevation={0} sx={{ p: 4, textAlign: "center", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          <Typography color="text.secondary">No hay cohortes abiertas a la espera de comisión.</Typography>
        </Paper>
      )}

      {grupos.map((g) => (
        <Box key={g.label} sx={{ mb: 3.5 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            {g.label}
          </Typography>
          <TablaKit columnas={COLUMNAS} minWidth={860} orden={orden} onSort={onSort}>
            <TableBody>
              {ordenar(g.cohortes, ACC_COHORTE, orden).map((c) => (
                <FilaCohorte
                  key={`${c.paso}-${c.ano}-${c.semestre}`}
                  c={c}
                  onDistribuir={() => navigate(`/app/comisiones/${c.paso}/${c.ano}/${c.semestre}`)}
                />
              ))}
            </TableBody>
          </TablaKit>
        </Box>
      ))}
    </Box>
  );
}

function FilaCohorte({ c, onDistribuir }: { c: CohorteComision; onDistribuir: () => void }) {
  const sinComision = c.tamano_comision === 0;
  return (
    <FilaKit>
      <CeldaPrincipal>{c.paso_nombre}</CeldaPrincipal>
      <CeldaKit>
        <Etiqueta variante="contorno">{c.tamano_comision} evaluadores</Etiqueta>
      </CeldaKit>
      <CeldaKit>{c.total}</CeldaKit>
      <CeldaKit atenuada={c.sin_entregar === 0}>{c.sin_entregar}</CeldaKit>
      <CeldaKit>{c.con_comision}</CeldaKit>
      <CeldaKit>
        {c.faltan > 0 ? <Etiqueta variante="acento">{c.faltan}</Etiqueta> : <Etiqueta variante="contorno">0</Etiqueta>}
      </CeldaKit>
      <CeldaKit align="right">
        <Button
          size="small"
          variant="contained"
          startIcon={<GroupsOutlined />}
          disabled={sinComision}
          onClick={onDistribuir}
        >
          Administrar
        </Button>
      </CeldaKit>
    </FilaKit>
  );
}
